import { DeviceType } from "@bitwarden/common/enums/deviceType";
import { PluginCardData } from "@bitwarden/web-vault/app/components/plugin-card/plugin-card.component";
import { GlossExtensionService } from "@bitwarden/web-vault/app/services/extension/gloss-extension.service";
import {
  AUTO_BUDGET_PLUGIN_ID,
  BUNDLE_PRICE_LABEL,
  GROCERY_PLUGIN_NAME,
  PluginIdentifier,
  RECURRING_DETECTOR_PLUGIN_ID,
  TRANSFER_CONVERSION_PLUGIN_NAME,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";

/**
 * The full plugin store catalog (installed or not). Shared between the Plugins page
 * (browse/install) and the global search bar (search across all plugins).
 */
export const PLUGIN_CATALOG: PluginCardData[] = [
  {
    id: GlossExtensionService.PLUGIN_ID,
    countries: ["australia"],
    isBetaAccess: false,
    title: "Gloss Vault",
    author: "By GLOSS Vault",
    rating: 5.0,
    description:
      "Browser extension that fetches live grocery prices from your own browser (residential, no CORS) — required for the Grocery price comparison feature.",
    logo: "images/logo-white-1024square-transparent.png",
    isLiked: false,
    downloads: 0,
    type: "Tools",
    permissions: [
      "Fetch grocery prices from Woolworths, Coles and Aldi",
      "Read the Gloss web app to relay price requests",
    ],
    version: "0.1.0",
    size: "—",
    lastUpdated: "31st Jul 2026",
    price: "Free",
  },
  {
    id: "plaid",
    countries: ["united-states"],
    title: "Plaid",
    author: "By Plaid",
    rating: 4.9,
    description:
      "Connect your bank accounts via Plaid and automatically fetch transactions and balances.",
    logo: "images/plaid.png",
    isLiked: false,
    downloads: 5200,
    type: "Finance",
    permissions: [
      "Read account transactions",
      "Access banking account data",
      "Fetch balances and transactions",
    ],
    version: "1.0.0",
    size: "—",
    lastUpdated: "17th Jun 2026",
    price: BUNDLE_PRICE_LABEL,
    isBetaAccess: false,
  },
  {
    id: "fiskil",
    countries: ["australia"],
    title: "Fiskil",
    author: "By Fiskil",
    rating: 4.9,
    description: "Connect your bank accounts via Fiskil open banking and fetch data automatically",
    logo: "images/fiskil.svg",
    isLiked: false,
    downloads: 5200,
    type: "Finance",
    permissions: [
      "Read account transactions",
      "Access banking account data",
      "Fetch balances and transactions",
    ],
    version: "1.0.0",
    size: "—",
    lastUpdated: "21st May 2026",
    price: BUNDLE_PRICE_LABEL,
    isBetaAccess: false,
  },
  {
    id: "opal-card", // ✅ Matches plugin config name "opal-card"
    countries: ["australia"],
    isBetaAccess: true,
    title: "Opal Card",
    author: "By GLOSS Vault",
    rating: 4.8,
    description: "Track your Opalcard transactions and balance automatically",
    isLiked: false,
    downloads: 1523,
    type: "Finance",
    permissions: ["Read account transactions", "Access location data", "Send notifications"],
    version: "2.3.1",
    size: "3.2 MB",
    lastUpdated: "2nd Jan 2026",
    price: "Free",
  },
  {
    // OLD: id: "superannuation-1"
    // NEW:
    id: "super", // ✅ Matches plugin config name "super"
    countries: ["australia"],
    isBetaAccess: true,
    title: "Superannuation",
    author: "By GLOSS Vault",
    rating: 4.9,
    description: "Monitor and manage your superannuation accounts in one place",
    isLiked: false,
    downloads: 2847,
    type: "Finance",
    permissions: ["Read account data", "Access superannuation information", "Generate reports"],
    version: "1.8.0",
    size: "4.1 MB",
    lastUpdated: "5th Jan 2026",
    price: "Free",
  },
  {
    // old: id: "crypto-sync-1", title: "crypto"
    id: "blockstream", // ✅ Matches plugin config name "blockstream"
    countries: ["australia", "hong-kong", "united-states"],
    isBetaAccess: true,
    title: "Crypto (Blockstream)",
    author: "By GLOSS Vault",
    rating: 4.6,
    description: "Sync your cryptocurrency portfolio and track real-time prices",
    isLiked: true,
    downloads: 3921,
    type: "Finance",
    permissions: [
      "Read account transactions",
      "Access cryptocurrency wallets",
      "Sync with exchanges",
    ],
    version: "3.0.2",
    size: "5.8 MB",
    lastUpdated: "7th Jan 2026",
    price: "Free",
  },
  {
    // OLD: id: "solana-sync-1", title: "solana"
    // NEW:
    id: "solana", // ✅ Matches plugin config name "solana"
    countries: ["australia", "hong-kong", "united-states"],
    isBetaAccess: true,
    title: "Solana",
    author: "By GLOSS Vault",
    rating: 4.6,
    description: "Sync your solana portfolio and track real-time prices",
    isLiked: true,
    downloads: 3921,
    type: "Finance",
    permissions: [
      "Read account transactions",
      "Access cryptocurrency wallets",
      "Sync with exchanges",
    ],
    version: "3.0.2",
    size: "5.8 MB",
    lastUpdated: "7th Jan 2026",
    price: "Free",
  },
  {
    // OLD: id: "ethvm-sync-1", title: "ethvm"
    // NEW:
    id: "ethvm", // ✅ Matches plugin config name "ethvm"
    countries: ["australia", "hong-kong", "united-states"],
    isBetaAccess: true,
    title: "ethVM",
    author: "By GLOSS Vault",
    rating: 4.6,
    description: "Track your Ethereum blockchain transactions with ethVM explorer",
    isLiked: true,
    downloads: 3921,
    type: "Finance",
    permissions: [
      "Read account transactions",
      "Access cryptocurrency wallets",
      "Sync with exchanges",
    ],
    version: "3.0.2",
    size: "5.8 MB",
    lastUpdated: "7th Jan 2026",
    price: "Free",
  },
  {
    // OLD: id: "house-price-1", title: "House Price Estimate"
    id: "gift-card",
    countries: ["australia", "hong-kong", "united-states"],
    isBetaAccess: true,
    title: "Gift Card",
    author: "By GLOSS Vault",
    rating: 4.7,
    description: "Track and manage your gift card balances",
    isLiked: false,
    downloads: 1876,
    type: "Finance",
    permissions: ["Access gift card data", "Track balances", "Send notifications"],
    version: "1.5.4",
    size: "2.9 MB",
    lastUpdated: "3rd Jan 2026",
    price: "Free",
  },
  {
    id: "chatbot",
    countries: ["australia", "hong-kong", "united-states"],
    // The chat only opens from the BETA_ACCESS-gated Ask AI pill in the search bar.
    isBetaAccess: true,
    title: "Chatbot",
    author: "By GLOSS Vault",
    rating: 4.9,
    description: "AI-powered chatbot to answer your finance questions",
    isLiked: false,
    downloads: 1876,
    type: "Tools",
    permissions: ["Access account data", "Generate responses", "Send notifications"],
    version: "1.5.4",
    size: "2.9 MB",
    lastUpdated: "7th May 2026",
    price: "Free",
  },
  {
    id: RECURRING_DETECTOR_PLUGIN_ID,
    countries: ["australia"],
    isBetaAccess: true,
    title: "Recurring Detector",
    author: "By GLOSS Vault",
    rating: 4.8,
    description:
      "Automatically detect recurring payments and subscriptions from your transaction history",
    isLiked: false,
    downloads: 0,
    type: "Analytics",
    permissions: ["Read account transactions"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "23rd Jun 2026",
    price: "Free",
  },
  {
    id: AUTO_BUDGET_PLUGIN_ID,
    countries: ["australia"],
    isBetaAccess: true,
    title: "Auto-Create Budget",
    author: "By GLOSS Vault",
    rating: 4.8,
    description:
      "Automatically create a monthly budget for each of your categories, sized to your average spend over the last 12 months.",
    isLiked: false,
    downloads: 0,
    type: "Analytics",
    permissions: ["Read account transactions", "Create budgets"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "13th Oct 2026",
    price: "Free",
  },
  {
    id: "wealth-comparison",
    countries: ["australia"],
    isBetaAccess: true,
    title: "Wealth Comparison",
    author: "By GLOSS Vault",
    rating: 4.8,
    description:
      "Compare your total net wealth against the expected benchmark for your age and income. See how you stack up and get insights to improve your financial health.",
    isLiked: false,
    downloads: 0,
    type: "Tools",
    permissions: ["Read total wealth balance"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "16th Jun 2026",
    price: "Free",
  },
  {
    id: TRANSFER_CONVERSION_PLUGIN_NAME,
    countries: ["australia", "hong-kong", "united-states"],
    // Beta-gated until Phase 5.3 lands the transaction badges/link controls. Without those,
    // a non-beta user would see a default-on "Installed" plugin with no usable UI (and could
    // persist enabled: false into the vault). Flip to false when 5.2 and 5.3 ship together.
    isBetaAccess: true,
    title: "Transfers & Conversions",
    author: "By GLOSS Vault",
    rating: 4.8,
    description:
      "Automatically detect transfers between your accounts and currency conversions within an account, de-emphasise them so they don't distort your spending, and link or unlink pairs yourself.",
    isLiked: false,
    downloads: 0,
    type: "Finance",
    permissions: ["Read account transactions", "Link related transactions"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "11th Aug 2026",
    price: "Free",
  },
  {
    id: "eodhd",
    countries: ["australia", "hong-kong", "united-states"],
    title: "EODHD Market Data",
    author: "By EOD Historical Data",
    rating: 4.7,
    description:
      "Fetch daily price action data for global stocks, ETFs, and mutual funds via EODHD",
    isLiked: false,
    downloads: 8432,
    type: "Finance",
    permissions: [
      "Access global stock market indices",
      "Fetch historical price data banks",
      "Automate reference tracking metrics",
    ],
    version: "1.2.0",
    size: "—",
    lastUpdated: "28th May 2026",
    price: "Free",
    isBetaAccess: false,
  },
  {
    id: PluginIdentifier.TRANSACTION,
    countries: ["australia"],
    isBetaAccess: false,
    title: "Transaction Detector",
    author: "By GLOSS Vault",
    rating: 4.8,
    description:
      "Automatically detects and categorises grocery transactions from Coles, Woolworths, and Aldi",
    isLiked: false,
    downloads: 0,
    type: "Finance",
    permissions: ["Read transaction descriptions"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "11th Jun 2026",
    price: "Free",
  },
  {
    id: PluginIdentifier.MONTHLY_SUMMARY,
    countries: ["australia", "hong-kong", "united-states"],
    isBetaAccess: false,
    title: "Monthly Summary",
    author: "By GLOSS Vault",
    rating: 4.8,
    description: "Get an automatic monthly summary of your income, spending, and top categories.",
    isLiked: false,
    downloads: 0,
    type: "Analytics",
    permissions: ["Read account transactions", "Read account balances"],
    version: "1.0.0",
    size: "—",
    lastUpdated: "14th Aug 2026",
    price: "Free",
  },
  {
    id: GROCERY_PLUGIN_NAME,
    countries: ["australia"],
    isBetaAccess: false,
    title: "Grocery Price Comparison",
    author: "By GLOSS Vault",
    rating: 4.7,
    description:
      "Compare live grocery prices across Woolworths, Coles and Aldi, and build a shopping list. Requires the Gloss Vault browser extension to fetch prices from your own browser.",
    isLiked: false,
    downloads: 0,
    type: "Tools",
    permissions: [
      "Fetch grocery prices from Woolworths, Coles and Aldi",
      "Use the Gloss Vault browser extension to relay price requests",
    ],
    version: "1.0.0",
    size: "—",
    lastUpdated: "24th Sep 2026",
    price: "Free",
  },
  // {
  //   id: "stock-tracker-1",
  //   title: "Stock",
  //   author: "By GLOSS Vault",
  //   rating: 4.5,
  //   description: "Track your stock portfolio with real-time market data",
  //   isLiked: false,
  //   downloads: 4532,
  //   type: "Finance",
  //   permissions: ["Read account transactions", "Access stock market data", "Send price alerts"],
  //   version: "2.7.3",
  //   size: "4.5 MB",
  //   lastUpdated: "6th Jan 2026",
  //   price: "Free",
  // },
];

/**
 * Whether a plugin can be installed on the given device. The Gloss Vault browser extension can't be
 * installed in the native iOS/Android apps (#5613), so it is unavailable there. Defined once here so
 * every PLUGIN_CATALOG consumer (the Plugins page, global search results, and search-bar redirect)
 * applies the same rule instead of re-deriving it — missing one is what stranded a recent-search row
 * on native (#5948).
 */
export const isPluginAvailableOn = (pluginId: string, device: DeviceType): boolean =>
  pluginId !== GlossExtensionService.PLUGIN_ID ||
  (device !== DeviceType.iOS && device !== DeviceType.Android);
